import { useState } from "react";
import { useAppContext } from "@/App";
import { useTranslation } from "@/hooks/useTranslation";
import { useAssetConfig } from "@/hooks/useAssetConfig";
import {
  X, Hand, Download, Send, BarChart3, Clock, MessageSquare,
  Activity, Trash2
} from "lucide-react";

export default function DriverReviewModal({ driver, driverStats, onClose, onSubjectDetail }) {
  const { session, hubData } = useAppContext();
  const { t } = useTranslation(session?.lang);
  const ac = useAssetConfig(session);
  const { refreshData } = useAppContext();
  const [pingMessage, setPingMessage] = useState("");
  const stats = driverStats[driver.driver_name] || {};
  const { feedback } = hubData;
  const driverFb = (feedback || []).filter(f => f.driver_name === driver.driver_name);

  const handleHighFive = async () => {
    const api = (await import("@/lib/api")).default;
    await api.sendHighFive({ from_user: session.userName || "Planner", to_user: driver.driver_name, hub_id: session.hubId });
    refreshData();
  };

  const handleSendPing = async () => {
    if (!pingMessage.trim()) return;
    const api = (await import("@/lib/api")).default;
    await api.sendMessage({
      hub_id: session.hubId, from_user: session.userName || "Planner",
      to_user: driver.driver_name, message: pingMessage, msg_type: "PING"
    });
    setPingMessage("");
    refreshData();
  };

  const handleRemoveDriver = async () => {
    const api = (await import("@/lib/api")).default;
    await api.deleteDriver(driver.driver_name, session.hubId);
    onClose();
    refreshData();
  };

  return (
    <div className="fixed inset-0 z-[200] bg-white/95 backdrop-blur-sm flex items-center justify-center p-4 animate-fadeIn" data-testid="driver-review-modal">
      <div className="bg-slate-50 rounded-xl w-full max-w-3xl max-h-[90vh] overflow-hidden border border-slate-200 animate-scaleIn">
        <header className="bg-white p-6 flex justify-between items-center border-b border-slate-200">
          <div className="flex items-center gap-4">
            <div className={`w-14 h-14 rounded-xl flex items-center justify-center text-white font-black text-xl ${driver.status === "ACTIVE" ? "bg-lime-500 text-slate-950" : "bg-slate-700"}`}>
              {driver.driver_name[0]}
            </div>
            <div>
              <h3 className="text-xl font-black uppercase tracking-tight text-slate-900">{driver.driver_name}</h3>
              <div className="flex items-center gap-2 mt-1">
                <div className={`status-dot ${driver.status === "ACTIVE" ? "online" : "offline"}`} />
                <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">{driver.region}</span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={handleHighFive} className="p-3 bg-amber-500 hover:bg-amber-400 text-slate-950 rounded-xl transition-all active:scale-95" data-testid="modal-highfive-btn">
              <Hand className="w-5 h-5" />
            </button>
            <button onClick={() => onSubjectDetail({ type: "driver", subject: driver.driver_name })} className="p-3 bg-slate-100 hover:bg-slate-200 text-amber-400 rounded-xl transition-all active:scale-95" data-testid="driver-log-btn">
              <Download className="w-5 h-5" />
            </button>
            <button onClick={onClose} className="p-3 bg-slate-100 hover:bg-slate-200 text-slate-400 rounded-xl transition-all" data-testid="close-driver-modal">
              <X className="w-5 h-5" />
            </button>
          </div>
        </header>
        <div className="p-6 overflow-y-auto max-h-[70vh] grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <h4 className="text-xs font-bold text-slate-400 uppercase tracking-widest flex items-center gap-2">
              <BarChart3 className="w-4 h-4 text-lime-500" /> Performance
            </h4>
            <div className="grid grid-cols-2 gap-3">
              <div className="p-4 bg-white border border-slate-200 rounded-xl">
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">{ac.itemA}</span>
                <span className="text-xl font-black text-slate-900 font-mono">{stats.liters || 0}{ac.itemAUnit}</span>
              </div>
              <div className="p-4 bg-white border border-slate-200 rounded-xl">
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">{ac.itemBEnabled ? ac.itemB : t("stops")}</span>
                <span className="text-xl font-black text-slate-900 font-mono">{ac.itemBEnabled ? `${stats.adblue || 0}${ac.itemBUnit}` : (stats.tasks || []).length}</span>
              </div>
              <div className="p-4 bg-white border border-slate-200 rounded-xl">
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">{t("efficiency")}</span>
                <span className="text-xl font-black text-lime-600 font-mono">{stats.efficiency || 0}%</span>
              </div>
              <div className="p-4 bg-white border border-slate-200 rounded-xl">
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">{t("stops")}</span>
                <span className="text-xl font-black text-slate-900 font-mono">{stats.sites || 0}</span>
              </div>
            </div>
            <div className="pt-4 border-t border-slate-200 space-y-3">
              <h4 className="text-xs font-bold text-slate-400 uppercase tracking-widest flex items-center gap-2">
                <MessageSquare className="w-4 h-4 text-lime-500" /> {t("direct_communication")}
              </h4>
              <div className="relative">
                <textarea data-testid="ping-message-input" value={pingMessage} onChange={e => setPingMessage(e.target.value)}
                  placeholder={t("send_message_placeholder")}
                  className="w-full p-4 bg-white border border-slate-200 rounded-xl text-sm text-slate-900 h-28 outline-none focus:border-lime-500 resize-none font-medium placeholder:text-slate-400" />
                <button data-testid="send-ping-btn" onClick={handleSendPing} disabled={!pingMessage.trim()}
                  className="absolute bottom-3 right-3 p-3 bg-lime-500 text-slate-950 rounded-xl shadow-lg disabled:opacity-30 active:scale-90 transition-all">
                  <Send className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
          <div className="space-y-4">
            <h4 className="text-xs font-bold text-slate-400 uppercase tracking-widest flex items-center gap-2">
              <Clock className="w-4 h-4 text-lime-500" /> Route History
            </h4>
            <div className="space-y-3 max-h-[300px] overflow-y-auto pr-1">
              {(stats.tasks || []).length === 0 ? (
                <p className="text-xs text-slate-400 text-center py-8 font-bold uppercase tracking-wider">{t("no_stops_yet")}</p>
              ) : (
                (stats.tasks || []).slice(0, 8).map((task, i) => (
                  <div key={i} className="p-3 bg-white border border-slate-200 rounded-xl">
                    <div className="flex justify-between items-center mb-1">
                      <span className="text-xs font-bold text-slate-400 uppercase">{task.site_name || "Site"}</span>
                      <span className="text-[10px] font-mono text-slate-400">{task.completed_at ? new Date(task.completed_at).toLocaleTimeString([], {hour:'2-digit', minute:'2-digit'}) : ""}</span>
                    </div>
                    <div className="flex gap-2">
                      <span className="text-[10px] font-bold bg-slate-100 text-slate-400 px-2 py-0.5 rounded">{task.machine_number}</span>
                      <span className="text-[10px] font-bold bg-lime-50 text-lime-600 px-2 py-0.5 rounded">+{task.delivered_diesel || 0}L</span>
                    </div>
                  </div>
                ))
              )}
            </div>
            <button data-testid="remove-driver-btn" onClick={handleRemoveDriver}
              className="w-full py-3 bg-red-50 border border-red-200 text-red-500 rounded-xl text-xs font-bold uppercase tracking-wider hover:bg-red-600 hover:text-slate-900 transition-all flex items-center justify-center gap-2">
              <Trash2 className="w-4 h-4" /> Remove Driver
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
